import React, { useState, useEffect } from "react";
import api, { usersApi, projectsApi, checklistApi, salesClientsApi, unifiedProjectsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";
import { 
  Send, 
  FileText, 
  Eye, 
  CheckCircle2, 
  Upload,
  ListChecks,
  Loader2,
  Calendar,
  Hash,
  Link as LinkIcon,
  Mail,
  AlignLeft,
  Building2,
  Plus,
  Trash2,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const FIELD_ICONS = {
  checkbox: CheckCircle2,
  text: FileText,
  number: Hash,
  dropdown: ListChecks,
  file: Upload,
  date: Calendar,
  textarea: AlignLeft,
  url: LinkIcon,
  email: Mail,
};

const CreateClientRequestDialog = ({ projectId, taskId, serviceType, onCreated, trigger }) => {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectClient, setProjectClient] = useState(null); // Client from current project
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFields, setTemplateFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingTemplates, setFetchingTemplates] = useState(false);
  
  // Custom fields for custom requests (when no template selected)
  const [customFields, setCustomFields] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({ label: "", field_type: "text", is_required: false });
  
  const FIELD_TYPES = [
    { value: "text", label: "Text", icon: FileText },
    { value: "textarea", label: "Long Text", icon: AlignLeft },
    { value: "file", label: "File Upload", icon: Upload },
    { value: "url", label: "URL Link", icon: LinkIcon },
    { value: "email", label: "Email", icon: Mail },
    { value: "number", label: "Number", icon: Hash },
    { value: "date", label: "Date", icon: Calendar },
    { value: "checkbox", label: "Checkbox", icon: CheckCircle2 },
  ];
  
  const [formData, setFormData] = useState({
    client_id: "",
    project_id: projectId || "",
    task_id: taskId || "",
    request_type: "info_request",
    title: "",
    description: "",
    template_id: "",
    template_data: {},
  });

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  // Fetch templates when service type changes
  useEffect(() => {
    if (serviceType && open) {
      fetchTemplatesForService(serviceType);
    }
  }, [serviceType, open]);

  const fetchData = async () => {
    try {
      // Use salesClientsApi instead of usersApi for client list
      const [clientsRes, projectsRes] = await Promise.all([
        salesClientsApi.getAll(),
        projectsApi.getAll(),
      ]);
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
      
      // Auto-select client if project has one (from unified API)
      if (projectId) {
        try {
          const projectRes = await unifiedProjectsApi.getOne(projectId);
          const project = projectRes.data;
          if (project?.client_id) {
            setFormData(prev => ({ ...prev, client_id: project.client_id }));
            // Find client info to display
            const clientInfo = (clientsRes.data || []).find(c => c.id === project.client_id);
            setProjectClient(clientInfo || { id: project.client_id, name: project.client_name || "Unknown" });
          } else if (project?.client_name) {
            // Project has client_name but no client_id - try to find client by name
            const clientByName = (clientsRes.data || []).find(
              c => c.name?.toLowerCase() === project.client_name?.toLowerCase()
            );
            if (clientByName) {
              setFormData(prev => ({ ...prev, client_id: clientByName.id }));
              setProjectClient(clientByName);
            } else {
              // No matching client found - show name but mark as needing client selection
              setProjectClient({ id: null, name: project.client_name, needsSelection: true });
            }
          }
        } catch (e) {
          console.log("Could not fetch project for client auto-select:", e);
        }
      }

      // Fetch all client_request templates
      await fetchTemplatesForService(serviceType);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchTemplatesForService = async (svcType) => {
    setFetchingTemplates(true);
    try {
      const res = await checklistApi.getTemplates({ target: "client_request" });
      let filtered = res.data || [];
      
      // Filter by service type if provided
      if (svcType) {
        filtered = filtered.filter(t => 
          t.service_types?.length === 0 || t.service_types?.includes(svcType)
        );
      }
      
      setTemplates(filtered);
      
      // Auto-select first matching template
      if (filtered.length > 0 && !selectedTemplate) {
        selectTemplate(filtered[0]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setFetchingTemplates(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({ 
      ...prev, 
      template_id: template.id,
      title: template.name,
    }));
    
    // Initialize field values
    const fields = {};
    template.fields?.forEach(field => {
      fields[field.id] = field.default_value || "";
    });
    setTemplateFields(fields);
  };

  const updateFieldValue = (fieldId, value) => {
    setTemplateFields(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check client: either from formData or from projectClient
    const effectiveClientId = formData.client_id || projectClient?.id;
    if (!effectiveClientId || !formData.title) {
      toast.error("Client and title are required");
      return;
    }
    
    // Validate payment request fields
    if (formData.request_type === "payment_request") {
      if (!formData.payment_amount || formData.payment_amount <= 0) {
        toast.error("Payment amount is required");
        return;
      }
    }
    
    // NOTE: We do NOT validate template fields here because this is a Client Request
    // The fields shown are what the CLIENT needs to fill, not what we fill when sending
    // We only send the template_id so the client knows what to provide
    
    setLoading(true);
    try {
      const requestData = {
        ...formData,
        client_id: effectiveClientId, // Use effective client_id
        template_data: templateFields,
        // Include custom fields if no template selected
        custom_fields: !selectedTemplate && customFields.length > 0 ? customFields : null,
      };
      
      await api.post("/client-requests", requestData);
      toast.success("Request sent to client");
      setOpen(false);
      resetForm();
      if (onCreated) onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      project_id: projectId || "",
      task_id: taskId || "",
      request_type: "info_request",
      title: "",
      description: "",
      template_id: "",
      template_data: {},
      payment_amount: null,
      payment_currency: "USD",
      payment_due_date: "",
    });
    setSelectedTemplate(null);
    setTemplateFields({});
    setCustomFields([]);
    setShowAddField(false);
    setNewField({ label: "", field_type: "text", is_required: false });
    // Don't reset projectClient if we're in project context
    if (!projectId) {
      setProjectClient(null);
    }
  };

  const addCustomField = () => {
    if (!newField.label.trim()) {
      toast.error("Field label is required");
      return;
    }
    setCustomFields([...customFields, { 
      ...newField, 
      id: `custom_${Date.now()}`,
      label: newField.label.trim() 
    }]);
    setNewField({ label: "", field_type: "text", is_required: false });
    setShowAddField(false);
  };

  const removeCustomField = (fieldId) => {
    setCustomFields(customFields.filter(f => f.id !== fieldId));
  };

  // Render a preview of what the client will need to fill
  const renderFieldPreview = (field) => {
    const Icon = FIELD_ICONS[field.field_type] || FileText;

    return (
      <div key={field.id} className="flex items-center gap-2 py-1.5 px-2 bg-zinc-800/30 rounded">
        <Icon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
        <span className="text-sm text-zinc-300">{field.label}</span>
        {field.is_required && <span className="text-red-400 text-xs">*</span>}
        <Badge variant="outline" className="ml-auto text-[10px] h-5 bg-zinc-800 text-zinc-500 border-zinc-700">
          {field.field_type === "file" ? "upload" : field.field_type}
        </Badge>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-orange-500 hover:bg-orange-600">
            <Send className="h-4 w-4 mr-2" />
            Request Client Action
          </Button>
        )}
      </DialogTrigger>
      <DialogContent 
        className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          // Allow ESC only if form is not dirty
          if (formData.title || formData.description) {
            e.preventDefault();
            if (window.confirm("You have unsaved changes. Close anyway?")) {
              setOpen(false);
            }
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <Send className="h-5 w-5 text-orange-500" />
            Request Client Action
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Client Display/Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Client *</Label>
            {projectId && projectClient && !projectClient.needsSelection ? (
              // If we're in a project context and have a valid client, show it as readonly
              <div className="flex items-center gap-2 p-3 bg-zinc-800 border border-zinc-700 rounded-md">
                <Building2 className="h-4 w-4 text-sky-400" />
                <span className="text-zinc-50">{projectClient.name}</span>
                <Badge variant="outline" className="ml-auto text-xs bg-sky-500/10 text-sky-400 border-sky-500/30">
                  From Project
                </Badge>
              </div>
            ) : projectClient?.needsSelection ? (
              // Project has client_name but no matching client record - show selector with warning
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-md text-xs text-orange-400">
                  <Building2 className="h-3.5 w-3.5" />
                  Project has "{projectClient.name}" but no matching client record. Please select:
                </div>
                <Select
                  value={formData.client_id}
                  onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              // Otherwise show client selector (for standalone use)
              <Select
                value={formData.client_id}
                onValueChange={(v) => setFormData({ ...formData, client_id: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Template Selection */}
          {templates.length > 0 && (
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <ListChecks className="h-4 w-4" />
                Request Template
              </Label>
              <div className="flex flex-wrap gap-2">
                {templates.map((template) => (
                  <Badge
                    key={template.id}
                    variant="outline"
                    onClick={() => selectTemplate(template)}
                    className={cn(
                      "cursor-pointer transition-all",
                      selectedTemplate?.id === template.id
                        ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/50"
                        : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-cyan-500/30"
                    )}
                  >
                    {template.name.replace(" - Client Data Request", "").replace(" - Client Questionnaire", "")}
                  </Badge>
                ))}
                <Badge
                  variant="outline"
                  onClick={() => { 
                    setSelectedTemplate(null); 
                    setTemplateFields({}); 
                    setFormData(prev => ({ ...prev, template_id: "", title: "" }));
                  }}
                  className={cn(
                    "cursor-pointer",
                    !selectedTemplate
                      ? "bg-zinc-700 text-zinc-300 border-zinc-600"
                      : "bg-zinc-800 text-zinc-500 border-zinc-700 hover:border-zinc-600"
                  )}
                >
                  Custom
                </Badge>
              </div>
            </div>
          )}

          {/* Template Fields - Preview of what client will fill */}
          {selectedTemplate && selectedTemplate.fields?.length > 0 && (
            <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
              <p className="text-xs text-cyan-400 font-medium flex items-center gap-1">
                <ListChecks className="h-3 w-3" />
                Client will fill {selectedTemplate.fields.length} fields:
              </p>
              <div className="space-y-1">
                {selectedTemplate.fields.map(renderFieldPreview)}
              </div>
            </div>
          )}

          {/* Custom Request Fields (when no template selected) */}
          {!selectedTemplate && (
            <>
              {/* Request Type */}
              <div className="space-y-2">
                <Label className="text-zinc-400">Request Type *</Label>
                <Select
                  value={formData.request_type}
                  onValueChange={(v) => setFormData({ ...formData, request_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="info_request">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-sky-400" />
                        Information Request
                      </div>
                    </SelectItem>
                    <SelectItem value="work_review">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-orange-400" />
                        Work Review
                      </div>
                    </SelectItem>
                    <SelectItem value="approval_request">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        Approval Request
                      </div>
                    </SelectItem>
                    <SelectItem value="payment_request">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-amber-400" />
                        Payment Request
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label className="text-zinc-400">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Please provide floor plan references"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-zinc-400">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details for the client..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                />
              </div>

              {/* Payment Request Fields */}
              {formData.request_type === "payment_request" && (
                <div className="space-y-3 p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
                  <p className="text-xs text-amber-400 font-medium flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Payment Details
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">Amount *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.payment_amount || ""}
                        onChange={(e) => setFormData({ ...formData, payment_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">Currency *</Label>
                      <Select
                        value={formData.payment_currency || "USD"}
                        onValueChange={(v) => setFormData({ ...formData, payment_currency: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="UAH">UAH</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Payment Due Date</Label>
                    <Input
                      type="date"
                      value={formData.payment_due_date || ""}
                      onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                </div>
              )}

              {/* Custom Fields Section */}
              <div className="space-y-3 pt-2 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400">Fields Client Needs to Fill</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddField(true)}
                    className="h-7 text-xs border-zinc-700 text-zinc-400 hover:text-zinc-50"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Field
                  </Button>
                </div>

                {/* Add Field Form */}
                {showAddField && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-zinc-500 text-xs">Field Label *</Label>
                        <Input
                          value={newField.label}
                          onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                          placeholder="e.g., Floor Plans PDF"
                          className="bg-zinc-900 border-zinc-700 text-zinc-50 text-sm h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-xs">Field Type</Label>
                        <Select 
                          value={newField.field_type} 
                          onValueChange={(v) => setNewField({ ...newField, field_type: v })}
                        >
                          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {FIELD_TYPES.map(ft => (
                              <SelectItem key={ft.value} value={ft.value}>
                                <div className="flex items-center gap-2">
                                  <ft.icon className="h-3 w-3 text-zinc-500" />
                                  {ft.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                        <Checkbox
                          checked={newField.is_required}
                          onCheckedChange={(c) => setNewField({ ...newField, is_required: c })}
                          className="border-zinc-600"
                        />
                        Required field
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setShowAddField(false); setNewField({ label: "", field_type: "text", is_required: false }); }}
                          className="h-7 text-xs text-zinc-500"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={addCustomField}
                          className="h-7 text-xs bg-sky-600 hover:bg-sky-700"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Fields List */}
                {customFields.length > 0 ? (
                  <div className="space-y-1.5">
                    {customFields.map((field) => {
                      const FieldIcon = FIELD_ICONS[field.field_type] || FileText;
                      return (
                        <div key={field.id} className="flex items-center gap-2 py-1.5 px-2 bg-zinc-800/30 rounded group">
                          <FieldIcon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                          <span className="text-sm text-zinc-300">{field.label}</span>
                          {field.is_required && <span className="text-red-400 text-xs">*</span>}
                          <Badge variant="outline" className="ml-auto text-[10px] h-5 bg-zinc-800 text-zinc-500 border-zinc-700">
                            {field.field_type}
                          </Badge>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCustomField(field.id)}
                            className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : !showAddField && (
                  <p className="text-xs text-zinc-500 text-center py-2">
                    No fields added. Click "Add Field" to create fields the client needs to fill.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Additional Notes (always shown) */}
          {selectedTemplate && (
            <div className="space-y-2">
              <Label className="text-zinc-400">Additional Notes</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Any additional instructions for the client..."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
              />
            </div>
          )}

          {/* Info about timer */}
          <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <p className="text-xs text-orange-400">
              ⏱ A response timer will start once this request is sent. 
              The client will have 48 hours (configurable) to respond.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {loading ? "Sending..." : "Send Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateClientRequestDialog;
